import React, { useState, useMemo } from 'react';
import { 
  Wallet, 
  CreditCard, 
  RefreshCw, 
  FileText, 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  BarChart2,
  Settings,
  ArrowDownToLine
} from 'lucide-react';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';

// Type Definitions
interface EscrowAccount {
  id: string;
  name: string;
  balance: number;
  currency: string;
  status: 'active' | 'frozen' | 'suspended';
}

interface Transaction {
  id: string;
  date: string;
  type: 'deposit' | 'withdrawal' | 'fee' | 'refund';
  amount: number;
  currency: string;
  status: 'completed' | 'pending' | 'failed';
}

interface FeeStructure {
  transactionType: string;
  feePercentage: number;
  fixedFee: number;
}

// Constants
const DEFAULT_CURRENCY = {
  code: 'GHS',
  symbol: '₵'
};

const FinancialManagement: React.FC = () => {
  // Initial State Management
  useBreadcrumbs([
        { label: 'Escrow Management', href: '#' },
        { label: 'Escrow Financial Management' }
      ]);
  const [escrowAccounts, setEscrowAccounts] = useState<EscrowAccount[]>([
    {
      id: 'ESC001',
      name: 'Main Escrow Account',
      balance: 250000,
      currency: 'GHS',
      status: 'active'
    },
    {
      id: 'ESC002',
      name: 'Secondary Escrow',
      balance: 100000,
      currency: 'GHS',
      status: 'active'
    }
  ]);

  const [transactions, setTransactions] = useState<Transaction[]>([
    {
      id: 'TRX001',
      date: '2024-02-10',
      type: 'deposit',
      amount: 50000,
      currency: 'GHS',
      status: 'completed'
    },
    {
      id: 'TRX002',
      date: '2024-02-11',
      type: 'withdrawal',
      amount: 25000,
      currency: 'GHS',
      status: 'completed'
    },
    {
      id: 'TRX003',
      date: '2024-02-12',
      type: 'fee',
      amount: 1250,
      currency: 'GHS',
      status: 'completed'
    }
  ]);

  const [feeStructures, setFeeStructures] = useState<FeeStructure[]>([
    {
      transactionType: 'Credit Card',
      feePercentage: 2.9,
      fixedFee: 0.30
    },
    {
      transactionType: 'Bank Transfer',
      feePercentage: 1.5,
      fixedFee: 0.20
    }
  ]);

  // State for Modals and Interactions
  const [selectedAccount, setSelectedAccount] = useState<EscrowAccount | null>(null);
  const [selectedFeeStructure, setSelectedFeeStructure] = useState<FeeStructure | null>(null);
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [isFeeModalOpen, setIsFeeModalOpen] = useState(false);

  // Financial Calculations
  const financialSummary = useMemo(() => {
    const totalEscrowBalance = escrowAccounts.reduce((sum, account) => sum + account.balance, 0);
    
    const completedTransactions = transactions.filter(t => t.status === 'completed');
    const transactionVolume = completedTransactions.reduce((sum, transaction) => {
      return transaction.type === 'deposit' 
        ? sum + transaction.amount 
        : sum - transaction.amount;
    }, 0);

    const totalFees = completedTransactions
      .filter(t => t.type === 'fee')
      .reduce((sum, fee) => sum + fee.amount, 0);

    return {
      totalEscrowBalance,
      transactionVolume,
      totalFees
    };
  }, [escrowAccounts, transactions]);

  // Modal Components
  const AccountDetailsModal = () => {
    if (!selectedAccount) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-lg shadow-xl p-6 w-96">
          <h2 className="text-xl font-bold mb-4">Account Details</h2>
          <div className="space-y-3">
            <div>
              <span className="text-gray-500">Account Name</span>
              <p className="font-semibold">{selectedAccount.name}</p>
            </div>
            <div>
              <span className="text-gray-500">Account ID</span>
              <p>{selectedAccount.id}</p>
            </div>
            <div>
              <span className="text-gray-500">Balance</span>
              <p className="font-bold text-green-600">
                {DEFAULT_CURRENCY.symbol}{selectedAccount.balance.toLocaleString()}
              </p>
            </div>
            <div>
              <span className="text-gray-500">Status</span>
              <p className={`font-semibold ${
                selectedAccount.status === 'active' ? 'text-green-600' : 'text-red-600'
              }`}>
                {selectedAccount.status.toUpperCase()}
              </p>
            </div>
          </div>
          <div className="mt-6 flex justify-end space-x-2">
            <button 
              onClick={() => setIsAccountModalOpen(false)}
              className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const FeeStructureModal = () => {
    const [editedFeeStructure, setEditedFeeStructure] = useState<FeeStructure | null>(selectedFeeStructure);

    const handleSave = () => {
      if (editedFeeStructure) {
        const updatedStructures = feeStructures.map(structure => 
          structure.transactionType === editedFeeStructure.transactionType 
            ? editedFeeStructure 
            : structure
        );
        setFeeStructures(updatedStructures);
        setIsFeeModalOpen(false);
      }
    };

    if (!selectedFeeStructure) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-lg shadow-xl p-6 w-96">
          <h2 className="text-xl font-bold mb-4">Edit Fee Structure</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-gray-700">Transaction Type</label>
              <input 
                type="text" 
                value={editedFeeStructure?.transactionType ?? ''}
                readOnly
                className="w-full border rounded p-2 bg-gray-100"
              />
            </div>
            <div>
              <label className="block text-gray-700">Fee Percentage</label>
              <input 
                type="number" 
                step="0.1"
                value={editedFeeStructure?.feePercentage ?? ''}
                onChange={(e) => {
                  if (editedFeeStructure) {
                    setEditedFeeStructure({
                      ...editedFeeStructure,
                      feePercentage: parseFloat(e.target.value)
                    });
                  }
                }}
                className="w-full border rounded p-2"
              />
            </div>
            <div>
              <label className="block text-gray-700">Fixed Fee</label>
              <input 
                type="number" 
                step="0.01"
                value={editedFeeStructure?.fixedFee ?? ''}
                onChange={(e) => {
                  if (editedFeeStructure) {
                    setEditedFeeStructure({
                      ...editedFeeStructure,
                      fixedFee: parseFloat(e.target.value)
                    });
                  }
                }}
                className="w-full border rounded p-2"
              />
            </div>
          </div>
          <div className="mt-6 flex justify-end space-x-2">
            <button 
              onClick={() => setIsFeeModalOpen(false)}
              className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
            <button 
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render Methods
  const renderFinancialSummary = () => (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold text-gray-900 flex items-center mb-4">
        <BarChart2 className="mr-2 h-5 w-5 text-purple-500" /> 
        Financial Summary
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Escrow Balance</p>
              <p className="text-xl font-bold text-gray-900">
                {DEFAULT_CURRENCY.symbol}{financialSummary.totalEscrowBalance.toLocaleString()}
              </p>
            </div>
            <Wallet className="h-6 w-6 text-blue-500" />
          </div>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Transaction Volume</p>
              <p className={`text-xl font-bold ${
                financialSummary.transactionVolume >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {DEFAULT_CURRENCY.symbol}{Math.abs(financialSummary.transactionVolume).toLocaleString()}
                {financialSummary.transactionVolume >= 0 ? (
                  <TrendingUp className="inline ml-2 h-5 w-5 text-green-500" />
                ) : (
                  <TrendingDown className="inline ml-2 h-5 w-5 text-red-500" />
                )}
              </p>
            </div>
            <DollarSign className="h-6 w-6 text-green-500" />
          </div>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Fees Collected</p>
              <p className="text-xl font-bold text-gray-900">
                {DEFAULT_CURRENCY.symbol}{financialSummary.totalFees.toLocaleString()}
              </p>
            </div>
            <CreditCard className="h-6 w-6 text-purple-500" />
          </div>
        </div>
      </div>
    </div>
  );

  const renderEscrowAccounts = () => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center">
          <Wallet className="mr-2 h-5 w-5 text-blue-500" /> 
          Escrow Accounts
        </h2>
        <button className="text-blue-600 hover:text-blue-800 flex items-center">
          <RefreshCw className="mr-1 h-4 w-4" /> Refresh
        </button>
      </div>
      <div className="space-y-4">
        {escrowAccounts.map((account) => (
          <div 
            key={account.id} 
            className={`p-4 rounded-lg border cursor-pointer ${
              account.status === 'active' 
                ? 'border-green-200 bg-green-50 hover:bg-green-100' 
                : 'border-red-200 bg-red-50 hover:bg-red-100'
            }`}
            onClick={() => {
              setSelectedAccount(account);
              setIsAccountModalOpen(true);
            }}
          >
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-medium text-gray-900">{account.name}</h3>
                <p className="text-sm text-gray-500">ID: {account.id}</p>
              </div>
              <div className="text-right">
                <p className="text-xl font-bold text-gray-900">
                  {DEFAULT_CURRENCY.symbol}{account.balance.toLocaleString()}
                </p>
                <span className={`text-xs font-medium ${
                  account.status === 'active' 
                    ? 'text-green-600' 
                    : 'text-red-600'
                }`}>
                  {account.status.charAt(0).toUpperCase() + account.status.slice(1)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderFeeStructures = () => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center">
          <Settings className="mr-2 h-5 w-5 text-blue-500" /> 
          Fee Structures
        </h2>
        <button 
          className="text-blue-600 hover:text-blue-800 flex items-center"
          onClick={() => {
            setSelectedFeeStructure(null);
            setIsFeeModalOpen(true);
          }}
        >
          <FileText className="mr-1 h-4 w-4" /> Manage
        </button>
      </div>
      <div className="space-y-4">
        {feeStructures.map((fee) => (
          <div 
            key={fee.transactionType} 
            className="p-4 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100"
            onClick={() => {
              setSelectedFeeStructure(fee);
              setIsFeeModalOpen(true);
            }}
          >
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-medium text-gray-900">{fee.transactionType}</h3>
                <p className="text-sm text-gray-500">
                    Percentage Fee: {fee.feePercentage}%
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-900">
                    Fixed Fee: {DEFAULT_CURRENCY.symbol}{fee.fixedFee.toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
        ))}
      </div>
    </div>
  );

  const renderTransactions = () => (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center">
          <FileText className="mr-2 h-5 w-5 text-blue-500" /> 
          Recent Transactions
        </h2>
        <button className="text-blue-600 hover:text-blue-800 flex items-center">
          <ArrowDownToLine className="mr-1 h-4 w-4" /> Export
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Transaction ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {transactions.map((transaction) => (
              <tr key={transaction.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {transaction.id}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {transaction.date}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    transaction.type === 'deposit' 
                      ? 'bg-green-100 text-green-800'
                      : transaction.type === 'withdrawal'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {DEFAULT_CURRENCY.symbol}{transaction.amount.toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    transaction.status === 'completed'
                      ? 'bg-green-100 text-green-800'
                      : transaction.status === 'pending'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">
            Financial Management
          </h1>

          {/* Financial Summary */}
          {renderFinancialSummary()}

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Escrow Accounts */}
            <div>
              {renderEscrowAccounts()}
            </div>

            {/* Fee Structures */}
            <div>
              {renderFeeStructures()}
            </div>
          </div>

          {/* Transactions */}
          <div>
            {renderTransactions()}
          </div>

          {/* Modals */}
          {isAccountModalOpen && <AccountDetailsModal />}
          {isFeeModalOpen && <FeeStructureModal />}
        </div>
      </div>
    </div>
  );
};

export default FinancialManagement;